import React from 'react';
import { connect } from 'react-redux';
import Card from 'react-bootstrap/Card';
import { invoiceListClearFilters, invoiceListFetch, invoiceListSetPage, invoiceListUnload } from '../../lib/redux/actions/invoices';
import MASpinner from '../../components/common/MASpinner';
import Alert from 'react-bootstrap/Alert';
import Paginator from '../../components/common/Paginator';
import InvoiceListTable from './InvoiceListTable';
import BulkAssignInvoicesForm from '../../components/forms/Invoice/BulkAssignInvoicesForm';
import InvoiceTableFilterForm from '../../components/forms/Invoice/InvoiceTableFilterForm';
import HospitalBulkChaseForm from '../../components/forms/Invoice/HospitalBulkChaseForm';
import { isChaserOutsourcer, isStaff } from '../../lib/helper/authorisation';
import * as ROUTES from '../../lib/routing/frontend';
import { addToast } from '../../lib/redux/actions/toast';
import { getFiltersFromSession } from '../../lib/routing/filters';
import AppliedFiltersOutput from '../../components/common/AppliedFiltersOutput';
import { REFRESH_INTERVAL } from '../../lib/helper/general';

const mapStateToProps = (state) => ({
  ...state.invoices,
  ...state.stats,
});

const mapDispatchToProps = {
  invoiceListFetch,
  invoiceListSetPage,
  invoiceListUnload,
  invoiceListClearFilters,
  addToast,
};

class InvoiceListContainer extends React.Component {
  constructor(props) {
    super(props);

    let currentFilters = getFiltersFromSession(props.match.path, 'json');

    this.state = {
      showFilters: currentFilters ?? false,
      totalInvoicesBulkSelected: 0,
      bulkSelectedInvoices: [],
      revisitOrderBy: props.defaultFilters?.['order[revisitDate]'] || null,
      invoiceDateOrderBy: props.defaultFilters?.['order[invoiceDate]'] || null,
      autoRefresh: true,
      lastUpdated: null,
      isInitialLoad: true,
      refreshInterval: REFRESH_INTERVAL,
      previousInvoiceIds: {}, // Track previous invoice IDs and their data for highlighting changes
      showHospitalBulkChaseForm: false,
    };
    const qs = new URLSearchParams(props.location.search);
    if (qs.has('assignedTo')) {
      this.props.filters['assignedTo[]'] = qs.get('assignedTo');
    }
    if (qs.has('revisitDate')) {
      this.props.filters['revisitDate'] = qs.get('revisitDate');
    }
  }

  getCombinedFilters() {
    const { filters, defaultFilters = {}, match } = this.props;
    let filtersFromSession = getFiltersFromSession(match.path, 'json');
    let returnFilters = { ...defaultFilters, ...filters, ...filtersFromSession };
    if (filtersFromSession && filtersFromSession['outstandingBalance(gte)'] !== 'undefined') {
      returnFilters['outstandingBalance(gte)'] = filtersFromSession['outstandingBalance(gte)'];
    }
    if (filtersFromSession && filtersFromSession['outstandingBalance(lte)'] !== 'undefined') {
      returnFilters['outstandingBalance(lte)'] = filtersFromSession['outstandingBalance(lte)'];
    }

    if (returnFilters['assignedTo']) {
      delete returnFilters['exists(assignedTo)'];
    }

    if (returnFilters['payorType']) {
      delete returnFilters['payorType.code'];
    }
    if (this.state.revisitOrderBy) {
      delete returnFilters['order[consultant.code]'];
      delete returnFilters['order[dateOfLastContact]'];
      delete returnFilters['order[patientLastName]'];
      delete returnFilters['order[invoiceDate]'];
      returnFilters['order[revisitDate]'] = this.state.revisitOrderBy;
    }
    if (this.state.invoiceDateOrderBy) {
      delete returnFilters['order[consultant.code]'];
      delete returnFilters['order[dateOfLastContact]'];
      delete returnFilters['order[patientLastName]'];
      delete returnFilters['order[revisitDate]'];
      returnFilters['order[invoiceDate]'] = this.state.invoiceDateOrderBy;
    }
    return returnFilters;
  }

  componentWillUnmount() {
    let persistFilters = this.props.match.path.includes('/invoices/:page');
    this.props.invoiceListUnload(persistFilters);

    // Clear auto-refresh interval when component unmounts
    this.clearAutoRefreshInterval();

    // Remove page visibility event listeners
    if (document.removeEventListener) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  componentDidMount() {
    // Only load invoices automatically if not on hospital bulk chase page
    // hospital bulk chase page should wait for filter application
    //    if (!this.isHospitalBulkChasePage()) {
    this.loadInvoices();
    //    }

    // Don't start auto-refresh on hospital bulk chase page
    if (!this.isHospitalBulkChasePage()) {
      this.startAutoRefreshInterval();
    }

    // Add page visibility event listener to pause polling when tab is not active
    if (document.addEventListener) {
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
  }

  // Handle page visibility changes to pause/resume polling
  handleVisibilityChange = () => {
    if (document.hidden) {
      // Page is hidden, pause polling
      this.clearAutoRefreshInterval();
    } else if (this.state.autoRefresh) {
      // Page is visible again and auto-refresh is enabled, resume polling
      this.startAutoRefreshInterval();
    }
  };

  // Toggle auto-refresh on/off
  toggleAutoRefresh() {
    this.setState(
      (prevState) => ({ autoRefresh: !prevState.autoRefresh }),
      () => {
        if (this.state.autoRefresh) {
          // Auto-refresh turned on, start polling
          this.startAutoRefreshInterval();
          this.props.addToast('Auto-refresh enabled', true);
        } else {
          // Auto-refresh turned off, stop polling
          this.clearAutoRefreshInterval();
          this.props.addToast('Auto-refresh disabled', true);
        }
      },
    );
  }

  // Start the auto-refresh interval
  startAutoRefreshInterval() {
    //    console.log('Start Auto-refreshing...');
    // Clear any existing interval first
    this.clearAutoRefreshInterval();

    // Set new interval
    this.autoRefreshTimer = setInterval(() => {
      //      console.log('Auto-refreshing...');
      // Only refresh if not already fetching
      if (!this.props.isFetchingInvoices) {
        this.refreshNow();
      }
    }, this.state.refreshInterval);
  }

  // Clear the auto-refresh interval
  clearAutoRefreshInterval() {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
  }

  // Manually refresh the invoice list
  refreshNow() {
    // Don't auto-refresh on hospital bulk chase page until filters are applied
    if (this.isHospitalBulkChasePage() && !this.hasHospitalSelected()) {
      return;
    }

    this.props
      .invoiceListFetch(this.props.currentPage, this.getCombinedFilters())
      .then(() => {
        // Update last updated timestamp
        this.setState({ lastUpdated: new Date() });
      })
      .catch((error) => {
        console.error('Error refreshing invoice list:', error);
        this.props.addToast('Error refreshing invoice list', false);
      });
  }

  loadInvoices(successMessage = false) {
    this.setState({
      totalInvoicesBulkSelected: 0,
      //      bulkSelectedInvoices: [],
    });
    this.props.invoiceListFetch(this.getQueryParamPage(), this.getCombinedFilters()).then(() => {
      // Update last updated timestamp
      this.setState({ lastUpdated: new Date() });

      if (successMessage !== false) {
        this.props.addToast('Invoices Assigned Successfully', true);
      }
    });
  }

  componentDidUpdate(prevProps) {
    const { currentPage, invoiceListFetch, invoiceListSetPage, defaultFilters, filters, invoiceListUnload, invoiceListClearFilters, invoices } = this.props;
    let persistFilters = this.props.match.path.includes('/invoices/:page');

    // Check if invoices have changed and track changes for highlighting
    if (prevProps.invoices !== invoices && invoices) {
      this.trackInvoiceChanges(prevProps.invoices, invoices);
    }

    if (prevProps.defaultFilters !== defaultFilters) {
      invoiceListUnload(persistFilters);
      if (!persistFilters) {
        invoiceListClearFilters();
      }
      this.loadInvoices();
      this.setState(() => {
        return { showFilters: false };
      });
    }

    if (prevProps.match.params.page !== this.getQueryParamPage()) {
      invoiceListSetPage(this.getQueryParamPage());
    }
    if (prevProps.currentPage !== currentPage || prevProps.filters !== filters) {
      // Don't fetch invoices on hospital bulk chase page until hospital is selected
      //      if (!this.isHospitalBulkChasePage() || this.hasHospitalSelected()) {
      invoiceListFetch(currentPage, this.getCombinedFilters());
      //      }

      // Reset bulk selected invoices when filters change
      if (prevProps.filters !== filters && this.state.bulkSelectedInvoices.length > 0) {
        this.setState({
          bulkSelectedInvoices: [],
          totalInvoicesBulkSelected: 0,
        });
      }
    }
  }

  // Track invoice changes to highlight new or updated invoices
  trackInvoiceChanges(prevInvoices, currentInvoices) {
    if (!prevInvoices || !currentInvoices) return;

    const updatedInvoiceIds = {};
    const newInvoiceIds = {};

    // Create a map of previous invoices for quick lookup
    const prevInvoiceMap = {};
    if (prevInvoices) {
      prevInvoices.forEach((invoice) => {
        prevInvoiceMap[invoice.id] = invoice;
      });
    }

    // Check each current invoice
    currentInvoices.forEach((invoice) => {
      if (!prevInvoiceMap[invoice.id]) {
        // This is a new invoice
        if (!this.state.isInitialLoad) {
          newInvoiceIds[invoice.id] = true;
        }
      } else {
        // Check if any important fields have changed
        const prevInvoice = prevInvoiceMap[invoice.id];
        if (
          invoice.outstandingBalance !== prevInvoice.outstandingBalance ||
          invoice.isPaid !== prevInvoice.isPaid ||
          invoice.isEscalated !== prevInvoice.isEscalated ||
          invoice.assignedTo?.id !== prevInvoice.assignedTo?.id ||
          invoice.dateOfLastContact !== prevInvoice.dateOfLastContact
        ) {
          // This invoice has been updated
          updatedInvoiceIds[invoice.id] = true;
        }
      }
    });

    // Store the changes in state
    this.setState({
      isInitialLoad: false,
      previousInvoiceIds: {
        new: newInvoiceIds,
        updated: updatedInvoiceIds,
        timestamp: new Date(),
      },
    });

    // Show toast notification if there are changes and auto-refresh is enabled
    const newCount = Object.keys(newInvoiceIds).length;
    const updatedCount = Object.keys(updatedInvoiceIds).length;

    if (this.state.autoRefresh && (newCount > 0 || updatedCount > 0)) {
      let message = '';
      if (newCount > 0 && updatedCount > 0) {
        message = `${newCount} new and ${updatedCount} updated invoices`;
      } else if (newCount > 0) {
        message = `${newCount} new invoice${newCount > 1 ? 's' : ''}`;
      } else if (updatedCount > 0) {
        message = `${updatedCount} invoice${updatedCount > 1 ? 's' : ''} updated`;
      }

      if (message) {
        this.props.addToast(message, true);
      }
    }
  }

  orderByRevisitDay() {
    const { currentPage, invoiceListFetch } = this.props;
    let revisitOrder = !this.state.revisitOrderBy ? 'ASC' : this.state.revisitOrderBy === 'ASC' ? 'DESC' : null;
    this.setState(
      {
        revisitOrderBy: revisitOrder,
        invoiceDateOrderBy: null,
      },
      () => {
        let filters = this.getCombinedFilters();
        invoiceListFetch(currentPage, filters);
      },
    );
  }

  orderByInvoiceDate() {
    const { currentPage, invoiceListFetch } = this.props;
    let invoiceDateOrder = !this.state.invoiceDateOrderBy ? 'ASC' : this.state.invoiceDateOrderBy === 'ASC' ? 'DESC' : null;
    this.setState(
      {
        invoiceDateOrderBy: invoiceDateOrder,
        revisitOrderBy: null,
      },
      () => {
        let filters = this.getCombinedFilters();
        invoiceListFetch(currentPage, filters);
      },
    );
  }

  getQueryParamPage() {
    return Number(this.props.match.params.page) || 1;
  }

  changePage(page) {
    const { history, invoiceListSetPage, path } = this.props;
    invoiceListSetPage(page);
    history.push(path.replace(':page', page));
  }

  nextPage() {
    const { currentPage, pageCount } = this.props;
    const newPage = Math.min(currentPage + 1, pageCount);
    this.changePage(newPage);
  }

  prevPage() {
    const { currentPage } = this.props;
    const newPage = Math.max(currentPage - 1, 1);
    this.changePage(newPage);
  }

  toggleFilters() {
    this.setState((prevState) => {
      return { showFilters: !prevState.showFilters };
    });
  }

  autoBulkSelectInvoices(count) {
    const { invoices } = this.props;

    this.setState({
      totalInvoicesBulkSelected: 0,
      bulkSelectedInvoices: [],
    });

    let invoicesToSelect = [];
    let newTotal = 0;

    invoices.slice(0, count).map((invoice) => {
      invoicesToSelect.push(invoice.id);
      newTotal++;
      return newTotal;
    });

    this.setState({
      totalInvoicesBulkSelected: newTotal,
      bulkSelectedInvoices: invoicesToSelect,
    });
  }

  invoiceBulkSelected(invoice) {
    const { totalInvoicesBulkSelected, bulkSelectedInvoices } = this.state;
    let invoicesToSelect = bulkSelectedInvoices;

    let newTotal;
    // if already selected, remove it
    if (bulkSelectedInvoices.some((invoiceId) => invoiceId === invoice.id)) {
      invoicesToSelect = invoicesToSelect.filter((invoiceId) => invoiceId !== invoice.id);
      newTotal = totalInvoicesBulkSelected - 1;
    } else {
      // otherwise add it
      invoicesToSelect.push(invoice.id);
      newTotal = totalInvoicesBulkSelected + 1;
    }
    this.setState({
      totalInvoicesBulkSelected: newTotal,
      bulkSelectedInvoices: invoicesToSelect,
    });
  }

  openHospitalBulkChaseForm = () => {
    this.setState({ showHospitalBulkChaseForm: true });
  };

  closeHospitalBulkChaseForm = () => {
    this.setState({ showHospitalBulkChaseForm: false });
  };

  isHospitalBulkChasePage = () => {
    return this.props.match.path === ROUTES.INVOICES.HOSPITAL_BULK_CHASE;
  };

  hasHospitalSelected = () => {
    const { filters = {} } = this.props;
    return filters['hospital.id'] && filters['hospital.id'].length > 0;
  };

  render() {
    const {
      invoices,
      stats,
      isFetchingInvoices,
      pageTitle,
      pageType,
      pageCount,
      currentPage,
      columnOverrides = [],
      showBulkSelect = false,
      enableBulkAssign = false,
      defaultFilters = {},
      filterOverrides = {},
    } = this.props;
    const { totalInvoicesBulkSelected = 0, bulkSelectedInvoices = [] } = this.state;
    let invoicesLeftClass = 'badge badge-pill badge-danger mr-3';
    return (
      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between">
            {' '}
            {pageTitle}
            {/*{invoices && invoices.length > 0 &&*/}
            <div>
              {pageType === 'bulk' && stats.creditControlInvoicesUnassigned && stats.creditControlInvoicesUnassigned > 0 ? (
                <span className={invoicesLeftClass} title="Invoices to assign">
                  {stats.creditControlInvoicesUnassigned}
                </span>
              ) : null}
              {pageType === 'all' && (isChaserOutsourcer(true) || isStaff(true)) && stats.invoicesCurrentlyAssigned && stats.invoicesCurrentlyAssigned > 0 ? (
                <span className={invoicesLeftClass} title="Invoices to process">
                  {stats.invoicesCurrentlyAssigned}
                </span>
              ) : null}
              <button className="btn btn-sm btn-outline-secondary mr-3" onClick={this.toggleFilters.bind(this)}>
                Filters
              </button>
              {!this.isHospitalBulkChasePage() && (
                <button
                  className={`btn btn-sm mr-3 ${this.state.autoRefresh ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={this.toggleAutoRefresh.bind(this)}
                  title={`${this.state.autoRefresh ? 'Disable' : 'Enable'} auto-refresh. Refreshes every ${this.state.refreshInterval / 1000} seconds. ${
                    this.state.lastUpdated ? `Last updated: ${new Date(this.state.lastUpdated).toLocaleTimeString()}.` : null
                  }`}>
                  <i className="fas fa-sync-alt mr-1"></i>
                  {this.state.autoRefresh ? 'Auto-Refresh On' : 'Auto-Refresh Off'}
                </button>
              )}

              {/*<button*/}
              {/*  className="btn btn-sm btn-outline-secondary mr-3"*/}
              {/*  onClick={this.refreshNow.bind(this)}*/}
              {/*  disabled={isFetchingInvoices}*/}
              {/*  title="Refresh now"*/}
              {/*>*/}
              {/*  <i className="fas fa-sync-alt mr-1"></i>*/}
              {/*  Refresh Now*/}
              {/*</button>*/}
              <span className="tabular-data-result-count">{pageCount} pages</span>
              {isFetchingInvoices && <MASpinner wrapperClasses="ml-3 float-right" size="sm" />}
            </div>
            {/*}*/}
          </div>
        </Card.Header>
        {this.isHospitalBulkChasePage() && (
          <Card.Body>
            <p>Filter by a Hospital and then select invoices you want to send.</p>
            <p>
              If you select invoices for multiple hospitals/consultants, then one email will be sent per hospital, per consultant with all their invoices
              attached.
            </p>
            {this.isHospitalBulkChasePage() && showBulkSelect && (
              <button
                className="btn btn-primary mr-3"
                onClick={this.openHospitalBulkChaseForm}
                disabled={bulkSelectedInvoices.length === 0}
                title={
                  bulkSelectedInvoices.length === 0
                    ? 'Select invoices to send hospital chaser email'
                    : `Send hospital chaser email for ${bulkSelectedInvoices.length} selected invoice(s)`
                }>
                <i className="fas fa-envelope mr-1"></i>
                Send Hospital Chaser Email
              </button>
            )}
          </Card.Body>
        )}
        {Boolean(this.state.showFilters) && (
          <Card.Body className={'mediaccount-filter-wrapper'}>
            <InvoiceTableFilterForm
              defaultFilters={defaultFilters}
              filterOverrides={filterOverrides}
              setPage={this.changePage.bind(this)}
              pageType={pageType}
              location={this.props.match.path}
            />
          </Card.Body>
        )}

        {showBulkSelect && enableBulkAssign && (
          <BulkAssignInvoicesForm
            showNextDayCount={false}
            invoiceIdsToAssign={bulkSelectedInvoices}
            numberOfInvoices={totalInvoicesBulkSelected}
            loadInvoices={this.loadInvoices.bind(this)}
            autoBulkSelectAction={this.autoBulkSelectInvoices.bind(this)}
          />
        )}

        {(invoices === null || invoices.length === 0) && (
          <Card.Body>
            <Alert variant={'info'} className="mb-0">
              No Invoices found
            </Alert>
          </Card.Body>
        )}

        <AppliedFiltersOutput />

        {invoices && invoices.length > 0 && (
          <InvoiceListTable
            columnOverrides={columnOverrides}
            invoices={invoices}
            showBulkSelect={showBulkSelect}
            bulkSelectedInvoices={bulkSelectedInvoices}
            bulkSelectAction={this.invoiceBulkSelected.bind(this)}
            pageType={pageType}
            orderByRevisitDay={this.orderByRevisitDay.bind(this)}
            orderByInvoiceDate={this.orderByInvoiceDate.bind(this)}
            revisitOrderBy={this.state.revisitOrderBy}
            invoiceDateOrderBy={this.state.invoiceDateOrderBy}
            changedInvoices={this.state.previousInvoiceIds}
          />
        )}
        {invoices && invoices.length > 0 && (
          <Paginator
            currentPage={currentPage}
            pageCount={pageCount}
            setPage={this.changePage.bind(this)}
            nextPage={this.nextPage.bind(this)}
            prevPage={this.prevPage.bind(this)}
          />
        )}

        <HospitalBulkChaseForm
          isOpen={this.state.showHospitalBulkChaseForm}
          onClose={this.closeHospitalBulkChaseForm}
          selectedInvoiceIds={bulkSelectedInvoices}
        />
      </Card>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceListContainer);
